import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil } from "lucide-react";
import { Button } from "components/common/Button";
import { Select } from "components/common/Select";
import { RouteSourceBadge } from "../RouteSourceBadge";
import { GroupVisibilityToggleButton } from "../GroupVisibilityToggleButton";
import type { RouteDetailsV3Props } from "./types";

export const RouteDetailsV3 = ({
  canEdit,
  route,
  routeGroupOptions,
  onChangeRouteType,
  onDelete,
  isPathEditing,
  isPathEditingDirty,
  isPathEditingSubmitting,
  pathEditingMeters,
  onStartPathEditing,
  onSubmitPathEditing,
  onCancelPathEditing,
  isRouteHidden,
  onToggleRouteVisibility,
}: RouteDetailsV3Props) => {
  const { t } = useTranslation();

  const [selectedCableType, setSelectedCableType] = useState(route.cable_type);
  const [isChanging, setIsChanging] = useState(false);

  const isCableTypeChanged = selectedCableType !== route.cable_type;

  const handleSaveCableType = useCallback(async () => {
    if (!isCableTypeChanged) return;
    setIsChanging(true);
    try {
      await onChangeRouteType(selectedCableType);
    } finally {
      setIsChanging(false);
    }
  }, [isCableTypeChanged, selectedCableType, onChangeRouteType]);

  const handleCancelCableType = useCallback(() => {
    setSelectedCableType(route.cable_type);
  }, [route.cable_type]);

  const handleEditPathToggle = useCallback(() => {
    if (isPathEditing) {
      onCancelPathEditing();
      return;
    }
    onStartPathEditing();
  }, [isPathEditing, onCancelPathEditing, onStartPathEditing]);

  const displayedMeters = isPathEditing
    ? pathEditingMeters
    : Number(route.meters);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto pt-1 space-y-3">
        <div className="grid grid-cols-2 gap-2 items-start">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("smartSurveyor.routeInspector.breakerLabel")}
            </div>
            <div className="text-sm text-gray-900 break-words">
              {route.breaker || "—"}
            </div>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("smartSurveyor.routeInspector.sourceLabel")}
            </div>
            <RouteSourceBadge creationSource={route.creation_source} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 items-start">
          <div>
            <div className="text-sm font-medium text-gray-700 mb-1">
              {t("smartSurveyor.routeInspector.metersLabel")}
            </div>
            <div className="text-sm text-gray-900 break-words">
              {displayedMeters > 0 ? `${displayedMeters.toFixed(2)} m` : "—"}
            </div>
          </div>
          <div className="self-end flex items-center justify-end gap-2">
            <GroupVisibilityToggleButton
              isHidden={isRouteHidden}
              onToggle={onToggleRouteVisibility}
              title={
                isRouteHidden
                  ? t("smartSurveyor.reviewPage.showRoute")
                  : t("smartSurveyor.reviewPage.hideRoute")
              }
            />
            {canEdit && (
              <Button
                variant={isPathEditing ? "primary" : "outline"}
                size="sm"
                onClick={handleEditPathToggle}
                disabled={isPathEditingSubmitting}
                startAdornment={<Pencil size={14} />}
              >
                {isPathEditing
                  ? t("smartSurveyor.routeInspector.editingPath")
                  : t("smartSurveyor.routeInspector.editPath")}
              </Button>
            )}
          </div>
        </div>
        <div className="pt-1">
          <div className="text-sm font-medium text-gray-700 mb-1">
            {t("smartSurveyor.routeInspector.cableTypeLabel")}
          </div>
          {canEdit ? (
            <Select
              value={selectedCableType}
              onChange={setSelectedCableType}
              options={routeGroupOptions}
              placeholder=""
              disabled={isChanging || isPathEditing}
            />
          ) : (
            <div className="text-sm text-gray-900 break-words">
              {selectedCableType || "—"}
            </div>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="shrink-0 pt-3 flex items-center justify-end gap-2 w-full">
          {isPathEditing ? (
            <>
              <Button
                className="flex-1"
                onClick={onCancelPathEditing}
                disabled={isPathEditingSubmitting}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={onSubmitPathEditing}
                disabled={!isPathEditingDirty || isPathEditingSubmitting}
              >
                {t("smartSurveyor.routeInspector.saveChanges")}
              </Button>
            </>
          ) : isCableTypeChanged ? (
            <>
              <Button
                className="flex-1"
                onClick={handleCancelCableType}
                disabled={isChanging}
              >
                {t("common.cancel")}
              </Button>
              <Button
                variant="primary"
                className="flex-1"
                onClick={handleSaveCableType}
                disabled={isChanging}
              >
                {t("common.save")}
              </Button>
            </>
          ) : (
            <Button variant="danger" onClick={onDelete}>
              {t("smartSurveyor.routeInspector.deleteRoute")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
